import React, { useState } from "react";
import { Container, Paper, Typography, Box, TextField, Button, Alert, Link, AppBar, Toolbar, Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

const PasswordReset = () => {
    const navigate = useNavigate();
    const { register, handleSubmit, formState: { errors } } = useForm();
    const { resetPassword } = useAuth();
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();

    const onSubmit = async (data) => {
        try {
            setError("");
            setMessage("");
            setLoading(true);
            await resetPassword(data.email);
            setMessage(t("checkEmailForReset"));
        } catch (err) {
            setError(t("resetPasswordError") + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Typography
                        variant="h6"
                        component="div"
                        onClick={() => navigate("/")}
                        sx={{
                            flexGrow: 1,
                            background: "linear-gradient(90deg, #DBC078 0%, #d0ad50 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            fontWeight: 900,
                            cursor: "pointer",
                            "&:hover": {
                                opacity: 0.8
                            }
                        }}
                    >
            CAPIVIO
                    </Typography>
                    <LanguageSwitcher />
                </Toolbar>
            </AppBar>
            <Container maxWidth="md">
                <Grid container spacing={3}>
                    {/* Left/Main Column */}
                    <Grid item xs={12} md={7}>
                        <Box
                            sx={{
                                minHeight: "100vh",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "linear-gradient(135deg, #155263 0%, #DBC078 100%)"
                            }}
                        >
                            <Paper
                                elevation={3}
                                sx={{
                                    p: 4,
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 3
                                }}
                            >
                                <Typography variant="h4" component="h1" align="center" gutterBottom>
                                    {t("resetPassword")}
                                </Typography>
                                {error && <Alert severity="error">{error}</Alert>}
                                {message && <Alert severity="success">{message}</Alert>}
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <TextField
                                            fullWidth
                                            label={t("email")}
                                            type="email"
                                            {...register("email", { 
                                                required: t("emailRequired"),
                                                pattern: {
                                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                    message: t("invalidEmail")
                                                }
                                            })}
                                            error={!!errors.email}
                                            helperText={errors.email?.message}
                                        />
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            fullWidth
                                            disabled={loading}
                                        >
                                            {loading ? t("sending") : t("resetPassword")}
                                        </Button>
                                    </Box>
                                </form>
                                <Box sx={{ textAlign: "center" }}>
                                    <Typography variant="body2">
                                        {t("rememberPassword")}{" "}
                                        <Link href="/login" underline="hover">
                                            {t("login")}
                                        </Link>
                                    </Typography>
                                </Box>
                            </Paper>
                        </Box>
                    </Grid>
                    {/* Right/Sidebar Column */}
                    <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 4, borderRadius: 4, mb: 3, background: "#fff", boxShadow: "0 4px 24px 0 rgba(21, 82, 99, 0.10)" }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: "#111", mb: 2 }}>
                Forgot your password?
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                Enter your email to receive password reset instructions and regain access to your account.
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </>
    );
};

export default PasswordReset; 